/**
 * Widget registry — maps each WidgetKey to its React component. Dashboard.tsx
 * reads this to decide what to render per layout item; falls back to
 * PlaceholderWidget if a key is registered in the catalog but not (yet)
 * mapped here. This is how future widgets get added — write the component,
 * register it here.
 */

import * as React from "react";
import type { LayoutItem, WidgetConfig, WidgetKey } from "../types";
import { PlaceholderWidget } from "./PlaceholderWidget";
import { AppointmentsTilesWidget } from "./AppointmentsTilesWidget";
import { InvoicesSummaryWidget } from "./InvoicesSummaryWidget";
import { EstimatesSummaryWidget } from "./EstimatesSummaryWidget";
import { RepairRequestsWidget } from "./RepairRequestsWidget";
import { RequestsByTypeWidget } from "./RequestsByTypeWidget";
import { ServiceRequestsListWidget } from "./ServiceRequestsListWidget";
import { ProjectsListWidget } from "./ProjectsListWidget";
import { RequestsWidget } from "./RequestsWidget";
import { DaysOffWidget } from "./DaysOffWidget";
import { SchedulesSummaryWidget } from "./SchedulesSummaryWidget";
import { AgingReportWidget } from "./AgingReportWidget";

export interface WidgetProps {
  widget: WidgetConfig;
  layout: LayoutItem;
}

export type WidgetComponent = React.ComponentType<WidgetProps>;

export const WIDGET_REGISTRY: Partial<Record<WidgetKey, WidgetComponent>> = {
  appointments_tiles: AppointmentsTilesWidget,
  invoices_summary: InvoicesSummaryWidget,
  estimates_summary: EstimatesSummaryWidget,
  repair_requests: RepairRequestsWidget,
  requests_by_type: RequestsByTypeWidget,
  service_requests_list: ServiceRequestsListWidget,
  projects_list: ProjectsListWidget,
  requests: RequestsWidget,
  days_off: DaysOffWidget,
  schedules_summary: SchedulesSummaryWidget,
  aging_report: AgingReportWidget,
};

export function resolveWidget(key: WidgetKey): WidgetComponent {
  return WIDGET_REGISTRY[key] ?? PlaceholderWidget;
}

export { PlaceholderWidget };
